import { useCallback, useEffect, useState } from "react"

import { IconCalendar } from "@tabler/icons-react"
import { format, parse } from "date-fns"
import { MaskedRange } from "imask"
import { DateRange, OnSelectHandler } from "react-day-picker"
import { P, match } from "ts-pattern"

import { cn } from "@/lib/utils"

import { Button, ButtonProps } from "./Button"
import { Calendar, CalendarProps } from "./Calendar"
import { InputProps, MaskedInput } from "./Input"
import { Popover, PopoverContent, PopoverTrigger } from "./Popover"

export type DatePickerProps = CalendarProps & {
  buttonProps?: ButtonProps<"button">
  inputProps?: InputProps
  onClose?: () => void
}

const dateFormat = "MM/dd/yyyy"
const inputMaskBlocks = {
  yyyy: {
    mask: MaskedRange,
    from: 1000,
    to: 9999,
  },
  MM: {
    mask: MaskedRange,
    from: 1,
    to: 12,
  },
  dd: {
    mask: MaskedRange,
    from: 1,
    to: 31,
  },
}

export function DatePicker({
  className,
  buttonProps,
  inputProps,
  onClose,
  ...props
}: DatePickerProps) {
  // We need to control the selected state here so that we can format the button labels appropriately
  const [selectedSingle, setSelectedSingle] = useState<Date | undefined>(
    props.mode === "single" ? props.selected : undefined,
  )
  const [selectedMultiple, setSelectedMultiple] = useState<Date[] | undefined>(
    props.mode === "multiple" ? props.selected : undefined,
  )
  const [selectedRange, setSelectedRange] = useState<DateRange | undefined>(
    props.mode === "range" ? props.selected : undefined,
  )

  const onSelectSingle = useCallback<OnSelectHandler<Date | undefined>>(
    (selected, ...rest) => {
      setSelectedSingle(selected)
      if (props.mode === "single") {
        if (selected === undefined) {
          if (!props.required) props.onSelect?.(selected, ...rest)
        } else {
          props.onSelect?.(selected, ...rest)
        }
      }
    },
    [props],
  )
  const onSelectMultiple = useCallback<OnSelectHandler<Date[]>>(
    (selected, ...rest) => {
      setSelectedMultiple(selected)
      if (props.mode === "multiple") {
        if (selected === undefined) {
          if (!props.required) props.onSelect?.(selected, ...rest)
        } else {
          props.onSelect?.(selected, ...rest)
        }
      }
    },
    [props],
  )
  const onSelectRange = useCallback<OnSelectHandler<DateRange>>(
    (selected, clickedDate, ...rest) => {
      setSelectedRange(selected)
      if (props.mode === "range") {
        if (selected === undefined) {
          if (!props.required) props.onSelect?.(selected, clickedDate, ...rest)
        } else {
          props.onSelect?.(selected, clickedDate, ...rest)
        }
      }
    },
    [props],
  )

  // Sync any external changes to props.selected to our internal selected state
  useEffect(() => {
    match(props)
      .with({ mode: "single", onSelect: P.nonNullable }, ({ selected }) => {
        setSelectedSingle((prev) => (prev !== selected ? selected : prev))
      })
      .with({ mode: "range", onSelect: P.nonNullable }, ({ selected }) => {
        setSelectedRange((prev) =>
          prev?.to !== selected?.to || prev?.from !== selected?.from ? selected : prev,
        )
      })
      .with({ mode: "multiple", onSelect: P.nonNullable }, ({ selected }) => {
        setSelectedMultiple((prev) =>
          prev?.length !== selected?.length ||
          selected?.some((date, i) => date !== prev?.[i])
            ? selected
            : prev,
        )
      })
      .otherwise(() => null)
  }, [props])

  return (
    <div className={cn("flex items-center gap-2", className)}>
      {match(props)
        .with({ mode: "single" }, () => (
          <MaskedInput
            mask={Date}
            lazy={false}
            value={selectedSingle ? format(selectedSingle, dateFormat) : undefined}
            format={(date) => format(date, dateFormat)}
            parse={(str) => parse(str, dateFormat, new Date())}
            pattern={dateFormat}
            blocks={inputMaskBlocks}
            onAccept={(_, mask) => {
              if (mask.unmaskedValue === "") {
                onSelectSingle(
                  undefined,
                  // @ts-expect-error: there is no date that triggered this
                  undefined,
                  {},
                  undefined,
                )
              }
              if (mask.typedValue instanceof Date) {
                const date = mask.typedValue
                onSelectSingle(
                  date,
                  date,
                  {},
                  // @ts-expect-error: there is no event to pass here
                  undefined,
                )
              }
            }}
            {...inputProps}
          />
        ))
        .with({ mode: "range" }, () => (
          <div className="flex w-full gap-2">
            <MaskedInput
              mask={Date}
              lazy={false}
              value={
                selectedRange?.from ? format(selectedRange.from, dateFormat) : undefined
              }
              format={(date) => format(date, dateFormat)}
              parse={(str) => parse(str, dateFormat, new Date())}
              pattern={dateFormat}
              blocks={inputMaskBlocks}
              onAccept={(_, mask) => {
                if (mask.typedValue instanceof Date) {
                  const date = mask.typedValue
                  onSelectRange(
                    {
                      from: date,
                      to: selectedRange?.to,
                    },
                    date,
                    {},
                    // @ts-expect-error: there is no event to pass here
                    undefined,
                  )
                }
              }}
              {...inputProps}
            />
            <span className="flex items-center"> to </span>
            <MaskedInput
              mask={Date}
              lazy={false}
              value={
                selectedRange?.to ? format(selectedRange.to, dateFormat) : undefined
              }
              format={(date) => format(date, dateFormat)}
              parse={(str) => parse(str, dateFormat, new Date())}
              pattern={dateFormat}
              blocks={inputMaskBlocks}
              onAccept={(_, mask) => {
                if (mask.typedValue instanceof Date) {
                  const date = mask.typedValue
                  onSelectRange(
                    {
                      from: selectedRange?.from,
                      to: date,
                    },
                    date,
                    {},
                    // @ts-expect-error: there is no event to pass here
                    undefined,
                  )
                }
              }}
              {...inputProps}
            />
          </div>
        ))
        .otherwise(() => null)}

      <Popover
        onOpenChange={(open) => {
          if (!open) {
            onClose?.()
          }
        }}
      >
        <PopoverTrigger asChild>
          {match(props)
            .with({ mode: P.union("single", "range") }, () => {
              return (
                <Button
                  iconOnly
                  icon={IconCalendar}
                  variant="neutral"
                  className="h-fit"
                  noBorder
                />
              )
            })
            .otherwise((props) => (
              <Button
                variant={props.mode && !props.selected ? "neutral" : "secondary"}
                icon={IconCalendar}
                size="small"
                {...buttonProps}
                className={cn(buttonProps?.className)}
              >
                {match(props)
                  .with({ mode: "multiple" }, () =>
                    selectedMultiple && selectedMultiple.length > 0 ? (
                      selectedMultiple.length > 3 ? (
                        `${selectedMultiple.length} days selected`
                      ) : (
                        selectedMultiple.map((d) => format(d, dateFormat)).join(", ")
                      )
                    ) : (
                      <span>Pick dates</span>
                    ),
                  )
                  .otherwise(() => null)}
              </Button>
            ))}
        </PopoverTrigger>
        <PopoverContent side="bottom" align="start" className="w-auto p-0">
          <Calendar
            {...props}
            fixedWeeks
            {...match(props)
              .with({ mode: "single" }, () => ({
                onSelect: onSelectSingle,
                selected: selectedSingle,
                defaultMonth: selectedSingle,
              }))
              .with({ mode: "multiple" }, () => ({
                onSelect: onSelectMultiple,
                selected: selectedMultiple,
              }))
              .with({ mode: "range" }, () => ({
                onSelect: onSelectRange,
                selected: selectedRange,
                defaultMonth: selectedRange?.from ?? selectedRange?.to,
              }))
              .otherwise(() => ({}))}
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}
